/* eslint-disable no-undef */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './TokenManager.css';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';
import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import vkbeautify from 'vkbeautify';
import { getApiKey } from '../../helpers/stringHelpers';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function TokenManager({ config }) {
    const { accounts, instance } = useMsal();
    const [formData, setFormData] = useState({
        client_id: '',
        client_secret: '',
        scope: '',
        grant_type: '',
    });

    const [tokenString, setTokenString] = useState('');
    const [showToken, setShowToken] = useState(false);
    const [appName, setAppName] = useState('');

    const [isGenerating, setIsGenerating] = useState(false);
    const [isCopying, setIsCopying] = useState(false);

    const [tokenInfo, setTokenInfo] = useState(null);
    const [error, setError] = useState('');

    const copyToClipboard = (text) => {
        setIsCopying(true);

        // Simulate a delay for copying
        setTimeout(() => {
            setIsCopying(false);
        }, 500); // Adjust the delay as needed

        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handlePaste = (event) => {
        event.preventDefault();
        const clipboardData = event.clipboardData.getData('text/plain');
        const pairs = clipboardData.split('&');

        const updatedFormData = { ...formData };

        pairs.forEach((pair) => {
            const [keyValuePair] = pair.split('&');
            const key = keyValuePair.split('=')[0];
            const value = keyValuePair.slice(key.length + 1);

            // Check if the key exists in the form data
            if (formData.hasOwnProperty(key)) {
                // Sanitize the value to remove '\r' and '\n' characters
                const sanitizedValue = value.replace(/[\r\n]/g, '');
                updatedFormData[key] = sanitizedValue;
            } else {
                // If there's no matching key, paste the entire pair into the input
                if (event.target.tagName === 'INPUT' && event.target.type === 'text') {
                    const inputName = event.target.name;
                    updatedFormData[inputName] = keyValuePair;
                }
            }
        });

        setFormData(updatedFormData);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        setIsGenerating(true);

        const applicationId = config.clientId;

        const accessTokenRequest = {
            scopes: ["api://" + applicationId + "/User.Read"],
            account: accounts[0],
        };
        instance
            .acquireTokenSilent(accessTokenRequest)
            .then((accessTokenResponse) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                const logmsgApiKey = getApiKey(config.enviromentScope);

                let decodeClientId = decodeURIComponent(formData.client_id);
                let decodeClientSecret = decodeURIComponent(formData.client_secret);
                let decodeScope = decodeURIComponent(formData.scope);
                let decodeGrantType = decodeURIComponent(formData.grant_type);

                let body = encodeURI("client_id=" + decodeClientId +
                    "&client_secret=" + decodeClientSecret +
                    "&scope=" + decodeScope +
                    "&grant_type=" + decodeGrantType);

                let logmsgApiUrl = config.logMsgApiUrl;
                
                axios.post(logmsgApiUrl + "/MakeTokenV2", body, {
                    headers: {
                        'Authorization': 'Bearer ' + accessToken,
                        'Content-Type': 'text/plain',
                        'APIKEY': logmsgApiKey,
                    },
                })
                    .then(response => {
                        var token = response.data.access_token;
                        setIsGenerating(false);
                        setTokenString(token);
                        setShowToken(true);
                    })
                    .catch(error => {
                        setIsGenerating(false);
                        setTokenString("");
                        setShowToken(false);
                        toast.error(<span>Token creation failed: <br/> Check if values are correct</span>);
                    });
            })
            .catch((error) => {
                setIsGenerating(false);
                setTokenString("");
                setShowToken(false);
                toast.error(<span>Token creation failed: <br /> Check if values are correct</span>);
            });
    };

    const fetchAppName = async (token) => {
        try {
            //console.log(token);
            var appId = token.azp;
            console.log(appId);
            const response = await axios.get(
                `https://graph.microsoft.com/v1.0/applications?$filter=appId eq '${appId}'`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.value.length > 0) {
                return response.data.value[0].displayName;
            } else {
                console.warn("App not found");
                return null;
            }
        } catch (error) {
            console.error("Failed to fetch app details:", error);
            return null;
        }
    };


    //Test Token String
    useEffect(() => {
        if (tokenString !== "") {
            try {
                const decodedToken = jwtDecode(tokenString);
                setTokenInfo(decodedToken);
                setError('');
                setShowToken(true);
                toast.success('Token created successfully');
            } catch (error) {
                setError("Invalid token");
                setTokenInfo(null);
                setShowToken(false);
                toast.error(<span>Token creation failed during decodeing: <br /> Check if values are correct</span>);
            }
        } else {
            setError('');
            setTokenInfo(null);
            setShowToken(false);
        }
    }, [tokenString]);

    useEffect(() => {
        if (tokenInfo !== null) {
            var appName = fetchAppName(tokenInfo);
            setAppName(appName);
        }    
    },[tokenInfo])

    return (
        <div className="token-manager-container">
            <h2 className="token-manager-title">Token Manager</h2>
            
            <div className="token-manager-content">
                <div className="token-form-section">
                    <form onSubmit={handleSubmit} onPaste={handlePaste}>
                        <div className="form-group">
                            <label htmlFor="client_id">Client Id:</label>
                            <input
                                type="text"
                                id="client_id"
                                name="client_id"
                                value={formData.client_id}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="client_secret">Client Secret:</label>
                            <input
                                type="text"
                                id="client_secret"
                                name="client_secret"
                                value={formData.client_secret}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="scope">Scope:</label>
                            <input
                                type="text"
                                id="scope"
                                name="scope"
                                value={formData.scope}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="grant_type">Grant Type:</label>
                            <input
                                type="text"
                                id="grant_type"
                                name="grant_type"
                                value={formData.grant_type}
                                onChange={handleInputChange}
                            />
                        </div>
                        <button className={`button generate-button ${isGenerating ? "clicked" : ""}`} type="submit">
                            {isGenerating ? "Generating..." : "Generate Token"}
                        </button>
                    </form>
                </div>

                <div className="token-display-section">
                    {showToken && (
                        <>
                            <div className="token-container">
                                <p className="token-string">{tokenString}</p>
                            </div>
                            <button className={`button copy-button ${isCopying ? "clicked" : ""}`} onClick={() => copyToClipboard(tokenString)}>
                                <i className="fas fa-copy"></i> {isCopying ? "Copying..." : "Copy to Clipboard"}
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="form-group" style={{ marginTop: '20px', maxWidth: '600px', margin: '20px auto 0' }}>
                <label htmlFor="token_test">Test Token:</label>
                <input
                    type="text"
                    id="token_test"
                    name="token_test"
                    value={tokenString}
                    onChange={(e) => setTokenString(e.target.value)}
                    placeholder="Paste a token here to test/decode it"
                />
            </div>

            {tokenInfo && (
                <div className="token-info-section">
                    <h2>Token Info</h2>
                    <pre>{vkbeautify.json(JSON.stringify(tokenInfo))}</pre>
                    <p><strong>Issued At:</strong> {new Date(tokenInfo.iat * 1000).toLocaleString()}</p>
                    <p><strong>Not Before:</strong> {new Date(tokenInfo.nbf * 1000).toLocaleString()}</p>
                    <p><strong>Expires At:</strong> {new Date(tokenInfo.exp * 1000).toLocaleString()}</p>
                </div>
            )}
            {error && <p style={{ color: 'red', textAlign: 'center' }}>{error}</p>}
            <ToastContainer />
        </div>
    );
}

TokenManager.propTypes = {
    config: PropTypes.shape({
        clientId: PropTypes.string.isRequired,
        logMsgApiUrl: PropTypes.string.isRequired,
        enviromentScope: PropTypes.string.isRequired,
    }).isRequired,
};

export default TokenManager;
